import { useQuery } from '@tanstack/react-query';
import { getTeamBranding } from '@/services/branding.service';
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { CalendarSettings } from '@/components/settings/CalendarSettings';
import { ChannelSettings } from '@/components/settings/ChannelSettings';
import { TeamSettings } from '@/components/settings/TeamSettings';
import { AffiliateSettings } from '@/components/settings/AffiliateSettings';
import { BillingSettings } from '@/components/settings/BillingSettings';
import { AIBrandProfileSettings } from '@/components/settings/AIBrandProfileSettings';
import { NotificationPreferences } from '@/components/notifications/NotificationPreferences';
import { InstallAppCard } from '@/components/pwa';
import { useAuth } from '@/hooks/useAuth';
import { usePackage } from '@/hooks/usePackage';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { LockBadge } from '@/components/gating/LockBadge';
import { cn } from '@/utils/cn';
import { formatFileSize } from '@/utils/mediaValidation';
import { authService } from '@/services/auth.service';
import { useTranslation } from 'react-i18next';
import { BrandScopedTab, NoBrandSelected } from '@/components/settings/BrandScopedTab';
import { storageCoveredByPool } from '@/utils/storageCoverage';
import { StoragePoolCard } from '@/components/settings/StoragePoolCard';

/**
 * Settings Page
 *
 * Modern, elegant user account settings and preferences
 */

interface UsageBarProps {
  current: number;
  limit: number | null; // null = unlimited
  colorClass?: string; // e.g., 'bg-blue-500', 'bg-green-500'
  /** Over the limit, but the overflow is absorbed by the Brand Portfolio pool: keep the neutral colour. */
  coveredByPool?: boolean;
}

const UsageBar = ({ current, limit, colorClass = 'bg-primary-500', coveredByPool = false }: UsageBarProps) => {
  // For unlimited (null), don't show a bar
  if (limit === null) return null;

  const percentage = Math.min((current / limit) * 100, 100);
  const isNearLimit = percentage >= 80 && !coveredByPool;
  const isAtLimit = percentage >= 100 && !coveredByPool;

  return (
    <div className="mt-1.5 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
      <div
        className={cn(
          'h-full rounded-full transition-all duration-300',
          isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : colorClass
        )}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

/**
 * Inline "Upgrade" hint shown next to a maxed-out plan limit.
 */
const UpgradeHint = () => {
  const { t } = useTranslation(['settings', 'common']);
  return (
    <Link
      to="/shop"
      state={{ entry: 'upsell' }}
      className="text-xs text-primary-600 ml-auto hover:underline"
    >
      {t('settings_upgrade_hint')}
    </Link>
  );
};

type SettingsTab = 'account' | 'channels' | 'calendars' | 'team' | 'notifications' | 'ai' | 'whitelabel' | 'affiliate' | 'billing';

/** Reviewer-only people: their own account, channels (connect/reconnect), notifications and affiliate. No brand settings. */
const REVIEWER_ONLY_TABS: readonly SettingsTab[] = ['account', 'channels', 'notifications', 'affiliate'];

export const Settings = () => {
  const { user } = useAuth();
  // Atelier status from the branding API (backend SSOT: package entitlement or add-on); same cache key as WhitelabelSettings
  const brandingTeamId = user?.currentTeamId ?? user?.teamId ?? null;
  const { data: brandingStatus } = useQuery({
    queryKey: ['branding', 'team', brandingTeamId],
    queryFn: () => getTeamBranding(brandingTeamId as number),
    enabled: brandingTeamId !== null,
    retry: false,
  });
  const { formatDateOnly } = useTimezone();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useUnsavedGuard(
    currentPassword.length > 0 || newPassword.length > 0 || confirmPassword.length > 0
  );

  // Package/plan data
  const {
    data: packageData,
    isLoading: packageLoading,
    getUsage,
    getLimit,
    hasFeature,
    isExpired
  } = usePackage();
  const { t } = useTranslation(['settings', 'common']);
  // Team-less agency staff get the agency Plan but no per-brand usage: hide meters/upgrade CTAs
  const { hasTeam } = useEffectiveTeamId();
  // Reviewer-only people (not agency staff in God Mode) get no brand settings tabs
  const { canAccessSettings } = useReviewerRestrictions();
  const reviewerOnly = !canAccessSettings;

  // Determine whether the active workspace is an agency-managed client space.
  // In God Mode the active team is user.currentTeam (always a client team, so isAgencyTeam is true).
  // In normal mode, check user.team.agencyId — non-null means it belongs to an agency.
  const isAgencyClientSpace: boolean = user
    ? (!!user.godModeSession
        ? (user.currentTeam?.isAgencyTeam ?? true)  // God Mode → always a client space
        : (user.team?.agencyId !== null && user.team?.agencyId !== undefined))
    : false;

  // Multi-brand agency portfolios manage upgrades/tokens collectively — hide the
  // per-brand-space upsell CTAs in that case. Single-brand portfolios (and
  // standalone accounts, where isAgencyClientSpace is false) keep the upsell.
  const hideBrandSpaceUpsell = isAgencyClientSpace && !!user?.portfolioIsMultiBrand;

  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    const tabParam = searchParams.get('tab') as SettingsTab | null;
    const validTabs: SettingsTab[] = ['account', 'channels', 'calendars', 'team', 'notifications', 'ai', 'whitelabel', 'affiliate', 'billing'];
    // A deep link to a tab the viewer does not have falls back to the account tab
    if (tabParam && reviewerOnly && !REVIEWER_ONLY_TABS.includes(tabParam)) {
      return 'account';
    }
    if (tabParam && validTabs.includes(tabParam)) {
      // Billing deep-link in an agency client space → fall back to account
      if (tabParam === 'billing' && isAgencyClientSpace) {
        return 'account';
      }
      return tabParam;
    }
    return 'account';
  });

  const handleTabChange = useCallback((tab: SettingsTab) => {
    // Whitelabel tab navigates directly to dedicated page
    if (tab === 'whitelabel') {
      navigate('/settings/whitelabel');
      return;
    }
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, navigate]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 8) {
      setPasswordError(t('error_password_too_short'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('error_passwords_mismatch'));
      return;
    }

    setIsChangingPassword(true);
    try {
      await authService.changePassword(currentPassword, newPassword, confirmPassword);
      setPasswordSuccess(t('settings_password_updated'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Billing of an agency-managed brand lives in the Brand Portfolio. When billing is requested (deep link, or the
  // live flip after a Plan purchase converted this team into a Brand Portfolio and auth/me was refreshed), an
  // OWNER/ADMIN of that Brand Portfolio lands on its billing with the purchase params preserved; everyone else
  // keeps the account-tab fallback.
  const canUsePortfolioBilling =
    !!user && !user.godModeSession && user.isAgencyStaff && user.agencyId !== null &&
    user.agencyId === (user.team?.agencyId ?? null) &&
    (user.agencyRole === 'OWNER' || user.agencyRole === 'ADMIN');
  const isTeamlessAgencyStaff = !!user?.isAgencyStaff && !hasTeam;
  const billingRequested = searchParams.get('tab') === 'billing' || activeTab === 'billing';
  useEffect(() => {
    if (!billingRequested) return;
    // Client space (owner/admin) or team-less agency staff: billing lives on the agency settings page
    const toAgencyBilling = (isAgencyClientSpace && canUsePortfolioBilling) || isTeamlessAgencyStaff;
    if (!toAgencyBilling) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'billing');
    navigate({ pathname: '/agency/settings', search: next.toString() }, { replace: true });
  }, [isAgencyClientSpace, canUsePortfolioBilling, isTeamlessAgencyStaff, billingRequested, searchParams, navigate]);

  useEffect(() => {
    if (searchParams.has('success') || searchParams.has('error')) {
      setActiveTab('channels');
    }
  }, [searchParams]);

  if (!user) {
    return null;
  }

  // Backend SSOT (package entitlement or add-on); the add-on flag keeps buyers working while it loads
  const hasWhitelabelAccess = (user.team?.hasWhitelabelAddon ?? false) || brandingStatus?.isWhitelabelEnabled === true;
  // Atelier tab stays visible when the plan lacks it (lock badge; its page shows the add-on screen). Only once
  // the branding status has answered, so a brand that has Atelier never flashes a lock while it loads.
  const whitelabelLocked = !hasWhitelabelAccess && brandingStatus !== undefined;
  // Check if user has admin access: system admin, super admin, team admin/owner, or God Mode
  const isGodMode = !!user.godModeSession;
  const isTeamAdmin = user.teamRole === 'OWNER' || user.teamRole === 'ADMIN';
  const isAdmin = user.role === 'ADMIN' || user.isSuperAdmin === true || isTeamAdmin || isGodMode;

  const allTabs = [
    { id: 'account' as const, label: t('settings_tab_account'), icon: ICONS.USER },
    { id: 'channels' as const, label: t('settings_tab_channels'), icon: ICONS.SHARE },
    { id: 'calendars' as const, label: t('settings_tab_calendars'), icon: ICONS.CALENDAR },
    ...(isAdmin ? [{ id: 'team' as const, label: t('settings_tab_team'), icon: ICONS.USERS }] : []),
    { id: 'notifications' as const, label: t('nav_notifications'), icon: ICONS.BELL },
    { id: 'ai' as const, label: t('settings_tab_ai', { defaultValue: 'AI' }), icon: ICONS.MAGIC_WAND },
    ...(hasWhitelabelAccess || whitelabelLocked
      ? [{ id: 'whitelabel' as const, label: t('settings_tab_whitelabel'), icon: ICONS.PAINT, locked: whitelabelLocked }]
      : []),
    { id: 'affiliate' as const, label: t('settings_tab_affiliate'), icon: ICONS.LINK },
    ...(!isAgencyClientSpace ? [{ id: 'billing' as const, label: t('settings_tab_billing'), icon: ICONS.CREDIT_CARD }] : []),
  ] as { id: SettingsTab; label: string; icon: string; locked?: boolean }[];
  const tabs = reviewerOnly ? allTabs.filter((tab) => REVIEWER_ONLY_TABS.includes(tab.id)) : allTabs;
  // Never render a tab the viewer does not have, whatever the state says
  const shownTab: SettingsTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : 'account';


  return (
    <div className="space-y-6 sm:space-y-8">
        {/* Page Header */}
        <PageHeader
          title={t('nav_settings')}
          subtitle={t('settings_subtitle')}
          icon={ICONS.SETTINGS}
          headerType="dashboard"
          documentTitle="Settings"
        />

        {/* Mobile: Icon grid with active label */}
        <div className="lg:hidden bg-gray-100/80 backdrop-blur-sm p-2 rounded-2xl">
          <div className="grid grid-cols-4 gap-1">
            {tabs.map((tab) => {
              const isActive = shownTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    'group flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl transition-all duration-200',
                    isActive
                      ? 'bg-white shadow-sm'
                      : 'hover:bg-white/50'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                  {...(tab.locked
                    ? { 'data-testid': 'locked-entry-whitelabel', 'aria-label': t('common:gating_locked_label', { feature: tab.label }) }
                    : {})}
                >
                  <span className="relative inline-flex">
                    <Icon
                      name={tab.icon}
                      size="sm"
                      color={isActive ? 'text-primary-600' : 'text-gray-500'}
                      ariaHidden
                    />
                    {tab.locked && <LockBadge size="xs" className="absolute -right-2 -bottom-1.5" />}
                  </span>
                  <span className={cn(
                    'text-[10px] font-medium leading-tight truncate max-w-full',
                    isActive ? 'text-gray-900' : 'text-gray-500'
                  )}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Desktop: pill tabs */}
        <div className="hidden lg:flex bg-gray-100/80 backdrop-blur-sm p-1.5 rounded-2xl gap-1 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                'group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 whitespace-nowrap',
                shownTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              )}
              aria-current={shownTab === tab.id ? 'page' : undefined}
              {...(tab.locked
                ? { 'data-testid': 'locked-entry-whitelabel', 'aria-label': t('common:gating_locked_label', { feature: tab.label }) }
                : {})}
            >
              <Icon
                name={tab.icon}
                size="sm"
                color={shownTab === tab.id ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}
                ariaHidden
              />
              {tab.label}
              {tab.locked && <LockBadge />}
            </button>
          ))}
        </div>

        {reviewerOnly && (
          <p className="text-sm text-gray-500">{t('settings_reviewer_only_hint')}</p>
        )}

        {/* Tab Content */}
        {shownTab === 'account' && (
          <div className="space-y-6">
            {/* Plan + Level Up paired — both subscription/upgrade themed.
                Entirely hidden for multi-brand agency client spaces, where upgrades/tokens
                are managed collectively at the portfolio level (no per-space plan/upgrade to show). */}
            {!hideBrandSpaceUpsell && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Hero Plan Card */}
                {packageLoading ? (
                  <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
                    <div className="p-5 sm:p-8">
                      <div className="animate-pulse space-y-4">
                        <div className="h-8 bg-gray-200 rounded w-1/3" />
                        <div className="space-y-3">
                          <div className="h-4 bg-gray-200 rounded" />
                          <div className="h-4 bg-gray-200 rounded w-2/3" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : packageData ? (
                  <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-mint shadow-lg">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
                    <div className="p-5 sm:p-8 text-center">
                      {/* Plan Icon */}
                      <div className="relative mx-auto mb-3 sm:mb-4 h-10 w-10 sm:h-16 sm:w-16">
                        <div className="absolute inset-0 rounded-2xl bg-primary-400 animate-pulse opacity-20" />
                        <div className="relative h-full w-full rounded-2xl bg-primary-400 flex items-center justify-center">
                          <Icon name={ICONS.CROWN} size="md" color="text-white" ariaHidden className="sm:!text-2xl" />
                        </div>
                      </div>

                      {/* Plan Name */}
                      <h2 className="text-lg sm:text-4xl font-bold text-gray-900 mb-3">{packageData.package.name}</h2>

                      {/* Badges */}
                      <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6">
                        {/* eslint-disable i18next/no-literal-string */}
                        {packageData.package.source === 'APPSUMO' && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                            AppSumo
                          </span>
                        )}
                        {/* eslint-enable i18next/no-literal-string */}
                        {packageData.package.source === 'TRIAL' && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
                            {t('package_source_trial')}
                          </span>
                        )}
                        {packageData.package.isAgency && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-coral-100 text-coral-700 border border-coral-200">
                            {t('package_type_agency')}
                          </span>
                        )}
                      </div>

                      {/* Expiry Warning */}
                      {packageData.expiresAt && (() => {
                        const daysUntilExpiry = Math.floor((new Date(packageData.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                        if (isExpired) {
                          return (
                            <div className="mb-4 sm:mb-6 p-4 rounded-xl bg-red-50 border border-red-200 max-w-md mx-auto">
                              <div className="flex items-center gap-3">
                                <Icon name={ICONS.CAUTION} size="sm" color="text-red-600" ariaHidden />
                                <div className="text-left">
                                  <p className="text-sm font-semibold text-red-900">{t('settings_plan_expired')}</p>
                                  <p className="text-xs text-red-700">{t('settings_expired_on', { date: formatDateOnly(packageData.expiresAt) })}</p>
                                </div>
                              </div>
                            </div>
                          );
                        } else if (daysUntilExpiry <= 30) {
                          return (
                            <div className="mb-4 sm:mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 max-w-md mx-auto">
                              <div className="flex items-center gap-3">
                                <Icon name={ICONS.CAUTION} size="sm" color="text-amber-600" ariaHidden />
                                <div className="text-left">
                                  <p className="text-sm font-semibold text-amber-900">{t('settings_expiring_in_days', { count: daysUntilExpiry })}</p>
                                  <p className="text-xs text-amber-700">{t('settings_renew_by', { date: formatDateOnly(packageData.expiresAt) })}</p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Upgrade CTA (needs a brand context) */}
                      {hasTeam && (
                      <Link
                        to="/shop"
                        state={{ entry: 'upsell' }}
                        className="inline-block px-4 sm:px-8 py-2 sm:py-3 text-sm sm:text-base bg-primary-400 hover:bg-primary-600 text-primary-400-contrast font-semibold rounded-lg sm:rounded-xl hover:shadow-xl transition-all duration-200"
                        aria-label="Upgrade to a higher plan"
                      >
                        {t('action_upgrade_plan')}
                      </Link>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="p-6">
                      <p className="text-gray-600 text-center">{t('settings_no_package')}</p>
                    </div>
                  </div>
                )}

                {/* Level Up Card — paired with Hero Plan. */}
                {packageData && hasTeam && (
                  <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-coral shadow-sm">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-coral" />
                    <div className="p-5 sm:p-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                        <Icon name={ICONS.STAR} size="sm" color="text-amber-600" ariaHidden />
                        {t('settings_level_up')}
                      </h3>
                      <ul className="space-y-4">
                        {/* Get More Tokens */}
                        <li>
                          <Link
                            to="/shop"
                            state={{ entry: 'upsell' }}
                            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-200 hover:border-amber-300 hover:shadow-sm transition-all group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-gradient-coral flex items-center justify-center">
                              <Icon name={ICONS.MAGIC_WAND} size="sm" color="text-amber-600" ariaHidden />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-900 group-hover:text-amber-700">{t('action_get_more_tokens')}</p>
                              <p className="text-xs text-gray-500">{t('action_get_more_tokens_desc')}</p>
                            </div>
                            <Icon name={ICONS.ARROW_RIGHT} size="sm" color="text-gray-400 group-hover:text-amber-600" ariaHidden />
                          </Link>
                        </li>
                        {/* Upgrade Package */}
                        <li>
                          <Link
                            to="/shop"
                            state={{ entry: 'upsell' }}
                            className="flex items-center gap-3 p-3 rounded-xl bg-primary-400 hover:bg-primary-600 text-primary-400-contrast hover:shadow-lg transition-all group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center">
                              <Icon name={ICONS.ARROW_UP} size="sm" color="text-primary-400-contrast" ariaHidden />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold">{t('action_upgrade_package')}</p>
                              <p className="text-xs text-primary-400-contrast opacity-80">{t('action_upgrade_package_desc')}</p>
                            </div>
                            <Icon name={ICONS.ARROW_RIGHT} size="sm" color="text-white/80" ariaHidden />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Grid of 3 Feature Cards (Level Up moved up to pair with Plan) */}
            {packageData && !hasTeam && <NoBrandSelected />}
            {packageData && !hasTeam && packageData.usage.portfolioStoragePool && (
              <StoragePoolCard pool={packageData.usage.portfolioStoragePool} />
            )}
            {packageData && hasTeam && (() => {
              // Extract limits once to avoid multiple calls and TypeScript null issues
              const maxUsers = getLimit('max_users');
              const maxChannels = getLimit('max_channels');
              const maxCalendars = getLimit('max_calendars');
              const maxStorage = getLimit('max_storage');
              const maxVariations = getLimit('max_variations');
              const maxAITokens = getLimit('max_ai_tokens');

              const usageUsers = getUsage('max_users');
              const usageChannels = getUsage('max_channels');
              const usageCalendars = getUsage('max_calendars');
              const usageStorage = getUsage('max_storage');
              // Brand inside a Brand Portfolio: what exceeds the brand's included storage is drawn from the shared pool.
              const storagePool = packageData.usage.storagePool;
              const storageOverIncluded = maxStorage !== null && usageStorage >= maxStorage;
              const storageIsCoveredByPool = storageCoveredByPool(usageStorage, maxStorage, storagePool);
              const usageAITokens = getUsage('max_ai_tokens');
              const usagePosts = getUsage('posts');
              const aiTokensAddonRemaining = packageData.usage.aiTokensAddonRemaining ?? 0;
              // Effective capacity = used + server-computed remaining (plan allowance first, then packs).
              // Falls back to base + addon for older backends that lack aiTokensRemaining.
              const aiTokensRemaining = packageData.usage.aiTokensRemaining;
              const effectiveAITokens = maxAITokens !== null
                ? (aiTokensRemaining !== undefined
                    ? usageAITokens + aiTokensRemaining
                    : maxAITokens + aiTokensAddonRemaining)
                : null;

              return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Card 1: Fäncy Connections */}
                <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
                  <div className="p-5 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                      <Icon name={ICONS.LINK} size="sm" color="text-primary-600" ariaHidden />
                      {t('settings_fancy_connections')}
                    </h3>
                    <ul className="space-y-3">
                      {/* Magic Ed (GPT-4) - Always active */}
                      <li className="flex items-center gap-2 text-sm">
                        <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-600" ariaHidden />
                        <span className="text-gray-900 font-medium">{t('settings_magic_ed')}</span>
                        <span className="text-gray-500 text-xs">{t('settings_gpt4')}</span>
                      </li>
                      {/* Unlimited Channels */}
                      <li className="flex flex-col gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-600" ariaHidden />
                          <span className="text-gray-900">
                            <span className="font-medium">{usageChannels}</span>
                            <span className="text-gray-500">
                              {' '}
                              {maxChannels === null
                                ? t('settings_usage_of_unlimited', { current: '' })
                                : t('settings_usage_of_limit', { current: '', limit: maxChannels })}
                              {' '}
                              {t('settings_channels')}
                            </span>
                          </span>
                        </div>
                        <UsageBar current={usageChannels} limit={maxChannels} />
                      </li>
                      {/* Canva */}
                      <li className={cn(
                        "flex items-center gap-2 text-sm",
                        !hasFeature('canva_enabled') && "opacity-50"
                      )}>
                        <Icon
                          name={hasFeature('canva_enabled') ? ICONS.CHECK_CIRCLE : ICONS.CROSS_CIRCLE}
                          size="sm"
                          color={hasFeature('canva_enabled') ? "text-green-600" : "text-gray-400"}
                          ariaHidden
                        />
                        <span className={hasFeature('canva_enabled') ? "text-gray-900" : "text-gray-500"}>{t('settings_canva')}</span>
                        {!hasFeature('canva_enabled') && (
                          <UpgradeHint />
                        )}
                      </li>
                      {/* Unsplash - Always active */}
                      <li className="flex items-center gap-2 text-sm">
                        <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-600" ariaHidden />
                        <span className="text-gray-900">{t('settings_unsplash')}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Card 2: Users + Däta Space */}
                <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-coral" />
                  <div className="p-5 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                      <Icon name={ICONS.USERS} size="sm" color="text-green-600" ariaHidden />
                      {t('settings_users_data_space')}
                    </h3>
                    <ul className="space-y-3">
                      {/* Users */}
                      <li className={cn(
                        "flex flex-col gap-2 text-sm",
                        maxUsers !== null && usageUsers >= maxUsers && "text-red-600"
                      )}>
                        <div className="flex items-center gap-2">
                          <Icon
                            name={maxUsers !== null && usageUsers >= maxUsers ? ICONS.CAUTION : ICONS.CHECK_CIRCLE}
                            size="sm"
                            color={maxUsers !== null && usageUsers >= maxUsers ? "text-red-600" : "text-green-600"}
                            ariaHidden
                          />
                          <span>
                            <span className="font-medium">{usageUsers}</span>
                            <span className="text-gray-500">
                              {' '}
                              {maxUsers === null
                                ? t('settings_usage_of_unlimited', { current: '' })
                                : t('settings_usage_of_limit', { current: '', limit: maxUsers })}
                              {' '}
                              {t('settings_users')}
                            </span>
                          </span>
                          {maxUsers !== null && usageUsers >= maxUsers && (
                            <UpgradeHint />
                          )}
                        </div>
                        <UsageBar current={usageUsers} limit={maxUsers} />
                      </li>
                      {/* Posts - Unlimited */}
                      <li className="flex items-center gap-2 text-sm">
                        <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-600" ariaHidden />
                        <span className="text-gray-900">
                          <span className="font-medium">{usagePosts.toLocaleString()}</span>
                          <span className="text-gray-500"> {t('settings_brand_assets_unlimited')}</span>
                        </span>
                      </li>
                      {/* Storage */}
                      <li className={cn(
                        "flex flex-col gap-2 text-sm",
                        storageOverIncluded && !storageIsCoveredByPool && "text-red-600"
                      )}>
                        <div className="flex items-center gap-2">
                          <Icon
                            name={storageOverIncluded && !storageIsCoveredByPool ? ICONS.CAUTION : ICONS.CHECK_CIRCLE}
                            size="sm"
                            color={storageOverIncluded && !storageIsCoveredByPool ? "text-red-600" : "text-green-600"}
                            ariaHidden
                          />
                          <span>
                            <span className="font-medium">{formatFileSize(usageStorage * 1024 * 1024)}</span>
                            <span className="text-gray-500">
                              {' '}
                              {t('settings_of')}
                              {' '}
                              {maxStorage !== null
                                ? formatFileSize(maxStorage * 1024 * 1024)
                                : t('settings_usage_of_unlimited', { current: '' })}
                            </span>
                          </span>
                          {storageOverIncluded && !storageIsCoveredByPool && (
                            <UpgradeHint />
                          )}
                        </div>
                        <UsageBar current={usageStorage} limit={maxStorage} coveredByPool={storageIsCoveredByPool} />
                        {storagePool && (storagePool.brandPoolUsedMb > 0 || storagePool.poolMb > 0) && (
                          <p className="text-xs text-gray-500">
                            {storagePool.brandPoolUsedMb > 0
                              ? t('settings_storage_pool_using', {
                                  used: formatFileSize(storagePool.brandPoolUsedMb * 1024 * 1024),
                                  pool: formatFileSize(storagePool.poolMb * 1024 * 1024),
                                  poolUsed: formatFileSize(storagePool.poolUsedMb * 1024 * 1024),
                                })
                              : t('settings_storage_pool_available', { pool: formatFileSize(storagePool.poolMb * 1024 * 1024) })}
                          </p>
                        )}
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Card 3: Ägile Posting & ÄI */}
                <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
                  <div className="p-5 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                      <Icon name={ICONS.MAGIC_WAND} size="sm" color="text-coral-600" ariaHidden />
                      {t('settings_agile_posting_ai')}
                    </h3>
                    <ul className="space-y-3">
                      {/* Calendars */}
                      <li className={cn(
                        "flex flex-col gap-2 text-sm",
                        maxCalendars !== null && usageCalendars >= maxCalendars && "text-red-600"
                      )}>
                        <div className="flex items-center gap-2">
                          <Icon
                            name={maxCalendars !== null && usageCalendars >= maxCalendars ? ICONS.CAUTION : ICONS.CHECK_CIRCLE}
                            size="sm"
                            color={maxCalendars !== null && usageCalendars >= maxCalendars ? "text-red-600" : "text-green-600"}
                            ariaHidden
                          />
                          <span>
                            <span className="font-medium">{usageCalendars}</span>
                            <span className="text-gray-500">
                              {' '}
                              {maxCalendars === null
                                ? t('settings_usage_of_unlimited', { current: '' })
                                : t('settings_usage_of_limit', { current: '', limit: maxCalendars })}
                              {' '}
                              {t('settings_calendars')}
                            </span>
                          </span>
                          {maxCalendars !== null && usageCalendars >= maxCalendars && (
                            <UpgradeHint />
                          )}
                        </div>
                        <UsageBar current={usageCalendars} limit={maxCalendars} />
                      </li>
                      {/* Variations */}
                      <li className={cn(
                        "flex items-center gap-2 text-sm",
                        maxVariations === 0 && "opacity-50"
                      )}>
                        <Icon
                          name={(maxVariations === null || (maxVariations !== null && maxVariations > 0)) ? ICONS.CHECK_CIRCLE : ICONS.CROSS_CIRCLE}
                          size="sm"
                          color={(maxVariations === null || (maxVariations !== null && maxVariations > 0)) ? "text-green-600" : "text-gray-400"}
                          ariaHidden
                        />
                        <span className={(maxVariations === null || (maxVariations !== null && maxVariations > 0)) ? "text-gray-900" : "text-gray-500"}>
                          <span className="font-medium">{maxVariations === null ? t('settings_unlimited') : maxVariations}</span>
                          {' '}
                          {t('settings_variations')}
                        </span>
                        {maxVariations === 0 && (
                          <UpgradeHint />
                        )}
                      </li>
                      {/* Brand Check */}
                      <li className={cn(
                        "flex items-center gap-2 text-sm",
                        !hasFeature('brand_check_enabled') && "opacity-50"
                      )}>
                        <Icon
                          name={hasFeature('brand_check_enabled') ? ICONS.CHECK_CIRCLE : ICONS.CROSS_CIRCLE}
                          size="sm"
                          color={hasFeature('brand_check_enabled') ? "text-green-600" : "text-gray-400"}
                          ariaHidden
                        />
                        <span className={hasFeature('brand_check_enabled') ? "text-gray-900" : "text-gray-500"}>
                          {t('settings_brand_check_view')}
                        </span>
                        {!hasFeature('brand_check_enabled') && (
                          <UpgradeHint />
                        )}
                      </li>
                      {/* AI Tokens */}
                      <li className={cn(
                        "flex flex-col gap-2 text-sm",
                        effectiveAITokens !== null && usageAITokens >= effectiveAITokens * 0.9 && "text-amber-600"
                      )}>
                        <div className="flex items-center gap-2">
                          <Icon
                            name={effectiveAITokens !== null && usageAITokens >= effectiveAITokens ? ICONS.CAUTION : ICONS.CHECK_CIRCLE}
                            size="sm"
                            color={effectiveAITokens !== null && usageAITokens >= effectiveAITokens ? "text-red-600" : effectiveAITokens !== null && usageAITokens >= effectiveAITokens * 0.9 ? "text-amber-600" : "text-green-600"}
                            ariaHidden
                          />
                          <span>
                            {t('settings_magic_ed_tokens')} <span className="font-medium">{usageAITokens.toLocaleString()}</span>
                            <span className="text-gray-500">
                              {' / '}
                              {t('settings_ai_tokens_usage', {
                                limit: effectiveAITokens !== null ? effectiveAITokens.toLocaleString() : t('settings_unlimited')
                              })}
                            </span>
                          </span>
                          {effectiveAITokens !== null && usageAITokens >= effectiveAITokens * 0.9 && (
                            <span className="text-xs text-primary-600 ml-auto">{t('settings_get_more_hint')}</span>
                          )}
                        </div>
                        <UsageBar current={usageAITokens} limit={effectiveAITokens} />
                        {aiTokensAddonRemaining > 0 && maxAITokens !== null && (
                          <span className="text-xs text-gray-500">
                            {t('settings_ai_tokens_breakdown', {
                              defaultValue: '{{base}} monthly + {{addon}} addon balance',
                              base: maxAITokens.toLocaleString(),
                              addon: aiTokensAddonRemaining.toLocaleString(),
                            })}
                          </span>
                        )}
                      </li>
                    </ul>
                  </div>
                </div>

              </div>
              );
            })()}

            {/* Password + Install App paired — both account utilities */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-stretch">
            {/* Password Change Section */}
            <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gray-500 to-gray-600" />
              <div className="p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <Icon name={ICONS.LOCK} size="sm" color="text-gray-600" ariaHidden />
                  {t('settings_change_password')}
                </h3>

                <form onSubmit={handlePasswordChange} className="space-y-4">
                  {passwordError && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      {passwordError}
                    </div>
                  )}
                  {passwordSuccess && (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                      {passwordSuccess}
                    </div>
                  )}

                  <div>
                    <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings_current_password')}
                    </label>
                    <div className="relative">
                      <input
                        id="current-password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={t('settings_enter_current_password')}
                        required
                        autoComplete="current-password"
                        className="w-full px-3 h-[34px] text-xs lg:h-auto lg:px-4 lg:py-3 lg:text-[12px] pr-10 rounded-lg border border-gray-300 ring-1 ring-transparent focus:outline-none focus:ring-primary-400 focus:border-primary-400 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 inset-y-0 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                        tabIndex={-1}
                        aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                      >
                        <span className="material-symbols-outlined !text-[18px]">
                          {showCurrentPassword ? 'visibility_off' : 'visibility'}
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings_new_password')}
                      </label>
                      <div className="relative">
                        <input
                          id="new-password"
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder={t('settings_enter_new_password')}
                          minLength={8}
                          required
                          autoComplete="new-password"
                          className="w-full px-3 h-[34px] text-xs lg:h-auto lg:px-4 lg:py-3 lg:text-[12px] pr-10 rounded-lg border border-gray-300 ring-1 ring-transparent focus:outline-none focus:ring-primary-400 focus:border-primary-400 transition-colors"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 inset-y-0 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                          tabIndex={-1}
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                        >
                          <span className="material-symbols-outlined !text-[18px]">
                            {showNewPassword ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings_confirm_new_password')}
                      </label>
                      <div className="relative">
                        <input
                          id="confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder={t('settings_confirm_new_password_placeholder')}
                          minLength={8}
                          required
                          autoComplete="new-password"
                          className="w-full px-3 h-[34px] text-xs lg:h-auto lg:px-4 lg:py-3 lg:text-[12px] pr-10 rounded-lg border border-gray-300 ring-1 ring-transparent focus:outline-none focus:ring-primary-400 focus:border-primary-400 transition-colors"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 inset-y-0 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                          tabIndex={-1}
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          <span className="material-symbols-outlined !text-[18px]">
                            {showConfirmPassword ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isChangingPassword}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-gray-900 text-white font-medium rounded-lg sm:rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isChangingPassword ? t('status_updating') : t('settings_change_password')}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Install App Card — paired with Password */}
            <InstallAppCard />
            </div>
          </div>
        )}

        {shownTab === 'channels' && <BrandScopedTab><ChannelSettings /></BrandScopedTab>}

        {shownTab === 'calendars' && <BrandScopedTab><CalendarSettings /></BrandScopedTab>}

        {shownTab === 'team' && <BrandScopedTab><TeamSettings /></BrandScopedTab>}

        {shownTab === 'notifications' && <NotificationPreferences />}

        {shownTab === 'ai' && <BrandScopedTab><AIBrandProfileSettings /></BrandScopedTab>}

        {shownTab === 'affiliate' && <AffiliateSettings />}

        {shownTab === 'billing' && !isAgencyClientSpace && !isTeamlessAgencyStaff && <BillingSettings />}
      </div>
  );
};
